import React from "react";
import { CheckCircle2, Clock, Users, Shield, TrendingUp, Map } from "lucide-react";
import { translations, LanguageCode } from "../utils/translations";

interface FieldServicePlannerProps {
  lang?: LanguageCode;
}

const featureIcons: React.ReactNode[] = [
  <Clock className="text-purple-600" size={24} />,
  <Map className="text-purple-600" size={24} />,
  <Users className="text-purple-600" size={24} />,
  <Shield className="text-purple-600" size={24} />,
  <TrendingUp className="text-purple-600" size={24} />,
  <CheckCircle2 className="text-purple-600" size={24} />,
];

const FieldServicePlanner: React.FC<FieldServicePlannerProps> = ({
  lang = "en",
}) => {
  const t = translations[lang].fieldServicePlannerPage;
  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "60px 24px", fontFamily: '"Inter", sans-serif' }}>
      {/* Hero Section */}
      <div style={{ textAlign: "center", marginBottom: "80px" }}>
        <h1 style={{ fontSize: "3.5rem", fontWeight: 900, color: "#111827", marginBottom: "24px", letterSpacing: "-1px" }}>
          {t.heroTitleLead} <span style={{ color: "#7c3aed" }}>{t.heroTitleHighlight}</span>
        </h1>
        <p style={{ fontSize: "1.25rem", color: "#4b5563", maxWidth: "800px", margin: "0 auto 40px", lineHeight: "1.6" }}>
          {t.heroBody}
        </p>
        <div style={{ display: "flex", gap: "16px", justifyContent: "center" }}>
          <button style={{ background: "#7c3aed", color: "#fff", padding: "14px 32px", borderRadius: "12px", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "1.1rem" }}>
            {t.getStarted}
          </button>
          <button style={{ background: "#fff", color: "#374151", border: "2px solid #e5e7eb", padding: "14px 32px", borderRadius: "12px", cursor: "pointer", fontWeight: 700, fontSize: "1.1rem" }}>
            {t.bookDemo}
          </button>
        </div>
      </div>

      {/* Benefits Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "32px", marginBottom: "100px" }}>
        {t.features.map((feature, index) => (
          <div key={index} style={{ background: "#fff", padding: "32px", borderRadius: "24px", border: "1px solid #f3f4f6", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.05)" }}>
            <div style={{ marginBottom: "20px" }}>{featureIcons[index]}</div>
            <h3 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#111827", marginBottom: "12px" }}>{feature.title}</h3>
            <p style={{ color: "#4b5563", fontSize: "1rem", lineHeight: "1.6" }}>{feature.description}</p>
          </div>
        ))}
      </div>

      {/* The Process Section */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "60px", alignItems: "center", marginBottom: "100px" }}>
        <div>
          <h2 style={{ fontSize: "2.25rem", fontWeight: 800, color: "#111827", marginBottom: "24px" }}>
            {t.processTitleLead} <br/><span style={{ color: "#7c3aed" }}>{t.processHighlight}</span>
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
            {t.steps.map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "20px" }}>
                <div style={{ fontSize: "1.5rem", fontWeight: 900, color: "#ddd", minWidth: "40px" }}>{item.step}</div>
                <div>
                  <h4 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#111827", marginBottom: "8px" }}>{item.title}</h4>
                  <p style={{ color: "#4b5563", lineHeight: "1.5" }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: "#714B67", padding: "40px", borderRadius: "32px", color: "#fff" }}>
          <h3 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "20px" }}>{t.whyChooseTitle}</h3>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "20px" }}>
            {t.benefits.map((benefit, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <CheckCircle2 size={20} className="text-teal-400" />
                <span style={{ fontSize: "1.1rem" }}>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Target Audience Section */}
      <div style={{ background: "#f9fafb", padding: "60px", borderRadius: "32px", textAlign: "center" }}>
        <h2 style={{ fontSize: "1.875rem", fontWeight: 800, color: "#111827", marginBottom: "40px" }}>{t.audienceTitle}</h2>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "24px" }}>
          {t.audiences.map((tag, i) => (
            <div key={i} style={{ background: "#fff", padding: "12px 24px", borderRadius: "12px", fontWeight: 600, color: "#374151", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)" }}>
              {tag}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FieldServicePlanner;
